import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { filterHash, getCachedSummary } from 'utils/emailCache';

import { API_URL } from 'config/api';
import { CONTEXT_SOURCE_AUTOGENERATED } from 'constants/strings';
import { User } from 'contexts/AuthContext';
import { UserContext } from 'hooks/settings/useContextManagement';
import { INBOX_CACHE_TTL_MS } from 'hooks/useEmailFetching';
import { InboxFilter } from 'hooks/useInboxFilters';
import { selectCategorySummary, selectLastFetchedAt } from 'store/selectors/emailSelectors';
import { setCategorySummary, setLoading, setSummaryLoading, setTotalCount } from 'store/slices/emailSlice';
import { AppDispatch } from 'store/store';

interface UseInboxInitializationProps {
  authLoading: boolean;
  user: User | null;
  mode: string;
  fetchEmails: (
    signalOrOverride?: AbortSignal | Partial<InboxFilter>,
    overrideFilters?: Partial<InboxFilter>
  ) => Promise<void>;
  fetchBatchStatus: (signal?: AbortSignal) => Promise<void>;
  fetchTabCounts: (force?: boolean, filters?: Partial<InboxFilter> | null, signal?: AbortSignal) => Promise<void>;
  filters?: Partial<InboxFilter> | null;
  /** Silent background refresh — does not show a loading spinner. Used for stale-while-revalidate. */
  refreshInPlace: (signal?: AbortSignal) => Promise<void>;
}

const ABORT_ERROR_NAME = 'AbortError';

/**
 * Check whether the inbox cache is considered fresh (i.e. fetched within the TTL window
 * and category data is already present in the store).
 */
function isCacheFresh(lastFetchedAt: number | null, hasCachedData: boolean): boolean {
  if (!lastFetchedAt || !hasCachedData) {
    return false;
  }
  return Date.now() - lastFetchedAt < INBOX_CACHE_TTL_MS;
}

export function useInboxInitialization({
  authLoading,
  user,
  mode,
  fetchEmails,
  fetchBatchStatus,
  fetchTabCounts,
  filters,
  refreshInPlace,
}: UseInboxInitializationProps) {
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [hasRunAnalysis, setHasRunAnalysis] = useState<boolean | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const lastFetchedAt = useSelector(selectLastFetchedAt);
  const categorySummary = useSelector(selectCategorySummary);
  const hasCachedData = categorySummary !== null && categorySummary.length > 0;

  // Ref-based callback pattern: captures the latest values of all inbox initialization
  // dependencies (dispatch, fetch functions, cache state) so the effect only re-runs
  // when the gating conditions change (authLoading, user, hasInitiallyLoaded) — not
  // every time a callback reference or Redux selector value changes.
  // (useEffectEvent does not exist in React 19.2 stable; this is the stable equivalent.)
  const runInitializationRef = useRef<(signal: AbortSignal) => Promise<void>>(async () => {});
  runInitializationRef.current = async (signal: AbortSignal) => {
    try {
      const shouldUseReduxCache = isCacheFresh(lastFetchedAt, hasCachedData);

      // Check localStorage for data persisted across page refreshes.
      // This handles the case where the user refreshes the page or opens a new tab.
      // Fix #1571 Bug 1: scope cache lookup to the current filter hash so that a stale
      // cache from a different filter configuration is never served on page reload.
      const currentFilterHash = filters ? filterHash(filters) : undefined;
      const localStorageSummary = !shouldUseReduxCache ? getCachedSummary(mode, Infinity, currentFilterHash) : null;
      const hasLocalStorageData = localStorageSummary !== null && localStorageSummary.length > 0;

      if (shouldUseReduxCache || hasLocalStorageData) {
        // If we only have localStorage data (e.g. after a page refresh), populate Redux
        // immediately so the UI renders without a spinner.
        if (hasLocalStorageData && !shouldUseReduxCache) {
          if (signal.aborted) {
return;
}
          dispatch(setCategorySummary(localStorageSummary));
          dispatch(setSummaryLoading(false));
          dispatch(setTotalCount(localStorageSummary.reduce((sum, cat) => sum + cat.count, 0)));
          dispatch(setLoading(false));
        }

        // Cache is fresh (Redux) or restored from localStorage: render immediately,
        // then silently revalidate in the background so the list stays up-to-date.
        if (!signal.aborted) {
          setHasInitiallyLoaded(true);
        }
        await Promise.all([
          refreshInPlace(signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error refreshing inbox in background:', err); 
} 
}),
          fetchBatchStatus(signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error fetching batch status:', err); 
} 
}),
          fetchTabCounts(false, filters, signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error fetching tab counts:', err); 
} 
}),

          axios
            .get(`${API_URL}/context`, { signal })
            .then(contextResponse => {
              const contexts = contextResponse.data || [];
              const hasAutogenerated = contexts.some((ctx: UserContext) => ctx.source === CONTEXT_SOURCE_AUTOGENERATED);
              setHasRunAnalysis(hasAutogenerated);
            })
            .catch(error => {
              if (signal.aborted) {
return;
}
              console.error('Error fetching contexts:', error);
              setHasRunAnalysis(false);
            }),
        ]);
      } else {
        // No cache at all — full fetch with loading indicator.
        // fetchEmails itself also checks localStorage (stale-while-revalidate),
        // so if localStorage has data it will show it immediately and refresh in background.
        await Promise.all([
          fetchEmails(signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error fetching emails:', err); 
} 
}),
          fetchBatchStatus(signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error fetching batch status:', err); 
} 
}),
          fetchTabCounts(true, filters, signal).catch(err => {
 if (!signal.aborted) {
 console.error('Error fetching tab counts:', err); 
} 
}),

          axios
            .get(`${API_URL}/context`, { signal })
            .then(contextResponse => {
              const contexts = contextResponse.data || [];
              const hasAutogenerated = contexts.some((ctx: UserContext) => ctx.source === CONTEXT_SOURCE_AUTOGENERATED);
              setHasRunAnalysis(hasAutogenerated);
            })
            .catch(error => {
              if (signal.aborted) {
return;
}
              console.error('Error fetching contexts:', error);
              setHasRunAnalysis(false);
            }),
        ]);

        // Set after fetch completes so the loading state is shown while data is being fetched.
        // Setting it early (before await) caused an empty state flash on re-navigation because
        // loading=false (persisted Redux) + hasInitiallyLoaded=true + categorySummary=null => EmptyState.
        if (!signal.aborted) {
          setHasInitiallyLoaded(true);
        }
      }
    } catch (err) {
      // AbortError is expected on cleanup (React StrictMode double-mount or dep change) — swallow silently.
      if (err instanceof Error && err.name === ABORT_ERROR_NAME) {
return;
}
      throw err;
    }
  };

  useEffect(() => {
    if (authLoading || !user || hasInitiallyLoaded) {
      return;
    }
    const controller = new AbortController();
    runInitializationRef.current(controller.signal);
    return () => controller.abort();
  }, [authLoading, user, hasInitiallyLoaded]);

  return { hasInitiallyLoaded, hasRunAnalysis };
}
